import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { WaveSine, Envelope, LockKey, User as UserIcon, EnvelopeSimpleOpen } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

function errText(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(' ');
  return String(detail);
}

export default function Register() {
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(null);

  const handleGoogle = () => {
    window.location.href = '/api/auth/google/start?next=%2Fdashboard';
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    setLoading(true);
    try {
      const { data } = await api.post('/auth/register', form);
      setSent({ email: form.email, devLink: data.verify_link });
    } catch (e) {
      toast.error(errText(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 mb-8 justify-center">
          <WaveSine size={26} weight="duotone" className="text-[#ccff00]" />
          <span className="font-display text-xl font-bold tracking-tight">TUNEAMPLIFIER</span>
        </Link>
        <div className="border border-zinc-800 rounded-2xl bg-zinc-900/50 p-8" data-testid="register-card">
          {!sent ? (
            <>
              <h1 className="font-display text-3xl font-bold">Start free.</h1>
              <p className="text-sm text-zinc-400 mt-1 mb-6">5 credits on the house — no card needed.</p>
              <form onSubmit={submit} className="space-y-3">
                <div className="relative">
                  <UserIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                  <Input data-testid="register-name-input" required placeholder="Your name" value={form.name}
                    onChange={e => setForm({ ...form, name: e.target.value })} className="pl-9 bg-zinc-950 border-zinc-800" />
                </div>
                <div className="relative">
                  <Envelope size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                  <Input data-testid="register-email-input" type="email" required placeholder="Email" value={form.email}
                    onChange={e => setForm({ ...form, email: e.target.value })} className="pl-9 bg-zinc-950 border-zinc-800" />
                </div>
                <div className="relative">
                  <LockKey size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                  <Input data-testid="register-password-input" type="password" required placeholder="Password (min. 8 characters)" value={form.password}
                    onChange={e => setForm({ ...form, password: e.target.value })} className="pl-9 bg-zinc-950 border-zinc-800" />
                </div>
                <Button data-testid="register-submit-btn" type="submit" disabled={loading} className="w-full btn-neon rounded-full font-display font-semibold h-11">
                  {loading ? 'Creating account…' : 'Create free account'}
                </Button>
              </form>
              <div className="flex items-center gap-3 my-6">
                <div className="flex-1 h-px bg-zinc-800" /><span className="text-xs font-mono text-zinc-600">OR</span><div className="flex-1 h-px bg-zinc-800" />
              </div>
              <button data-testid="register-google-btn" onClick={handleGoogle}
                className="w-full rounded-full border border-zinc-700 hover:border-zinc-500 py-2.5 text-sm font-mono transition-[border-color]">
                Continue with Google
              </button>
              <p className="text-center text-sm text-zinc-500 mt-6">
                Already have an account? <Link to="/login" data-testid="goto-login-link" className="text-[#ccff00] hover:underline">Log in</Link>
              </p>
            </>
          ) : (
            <div className="text-center" data-testid="register-check-inbox">
              <EnvelopeSimpleOpen size={40} weight="duotone" className="text-[#ccff00] mx-auto" />
              <h1 className="font-display text-2xl font-bold mt-4">Check your inbox.</h1>
              <p className="text-sm text-zinc-400 mt-2">
                We sent a verification link to <span className="text-zinc-100 font-mono">{sent.email}</span>. Click it to activate your account.
              </p>
              {sent.devLink && (
                <a href={sent.devLink} data-testid="dev-verify-link" className="mt-4 inline-block text-xs font-mono text-[#ccff00] underline break-all">{sent.devLink}</a>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
